import { useCallback, useEffect, useRef, useState } from "react";
import { getField } from "../fields";
import type { FieldValues, InputDef } from "../engine/types";
import { FormRenderer, clearPersisted } from "../engine/FormRenderer";
import { submittableValues } from "../engine/validate";
import { detect, useRun, type RunResult } from "../engine/useRun";
import { useSession } from "./session";
import { ResultView } from "./ResultView";
import { Banner, Button, Card, EmptyState, LinearProgress } from "./primitives";
import { Icon } from "./Icon";
import { Link, type Route } from "./router";

/**
 * The main screen. Form → run → result, with the form kept alive underneath so
 * "edit one answer and re-run" is one click away — the single biggest advantage
 * a form has over a chat box.
 */

type View = "form" | "result";

export function FieldScreen({
  fieldId,
  navigate,
  initialValues,
}: {
  fieldId: string;
  navigate: (r: Route) => void;
  /** Prefill from a history re-run. */
  initialValues?: FieldValues;
}) {
  const pack = getField(fieldId);
  const { quota, updateQuota, openAuth, user } = useSession();
  const { phase, result, error, progress, stageLabel, elapsedMs, start, reset } = useRun();
  const [view, setView] = useState<View>("form");
  const [prefill, setPrefill] = useState<FieldValues | undefined>(initialValues);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPrefill(initialValues);
  }, [initialValues]);

  // A quota wall must never be the user's first experience — the server decides
  // when they're out, and only then do we ask for an account.
  useEffect(() => {
    if (error?.code === "quota_exhausted") {
      openAuth("خلصت تشغيلاتك المجانية لهذا اليوم. سجّل دخول بالموبايل وكمّل.");
    }
  }, [error, openAuth]);

  const onDetect = useCallback(
    (input: InputDef, values: FieldValues) => detect(fieldId, input.name, values),
    [fieldId],
  );

  const handleSubmit = useCallback(
    async (values: FieldValues) => {
      if (!pack) return;
      const payload = submittableValues(pack.inputs, values);
      setView("result");
      const res = await start(fieldId, payload);
      if (res) {
        updateQuota(res.quota);
        // The form is done with; a fresh visit should start clean.
        if (res.status === "ok") clearPersisted(fieldId);
        window.requestAnimationFrame(() =>
          resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        );
      }
    },
    [pack, fieldId, start, updateQuota],
  );

  if (!pack) {
    return (
      <EmptyState
        icon="search_off"
        title="ما لكينا هذي الأداة"
        body={`ما في مجال بهذا الاسم: ${fieldId}`}
        action={
          <Button variant="tonal" icon="arrow_back" onClick={() => navigate({ name: "catalog" })}>
            رجوع للأدوات
          </Button>
        }
      />
    );
  }

  const remaining = quota?.remaining ?? null;

  return (
    <div className="grid gap-6">
      {/* ─── Header ─── */}
      <div className="md-enter">
        <Link
          to={{ name: "catalog" }}
          navigate={navigate}
          className="md-label-large mb-3 inline-flex items-center gap-1 text-on-surface-variant hover:text-primary"
        >
          <Icon name="arrow_forward" size={18} />
          كل الأدوات
        </Link>

        <div className="flex items-start gap-4">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-primary-container text-on-primary-container">
            <Icon name={pack.icon} size={30} filled />
          </span>
          <div className="min-w-0">
            <h1 className="md-headline-medium text-on-surface">{pack.title}</h1>
            <p className="md-body-medium mt-1 text-on-surface-variant">{pack.blurb}</p>
          </div>
        </div>
      </div>

      {/* ─── Pack notice (Law's "not legal advice" boundary) ─── */}
      {pack.notice ? (
        <NoticeBanner
          notice={pack.notice}
          navigate={navigate}
          // Coverage matters exactly when a pack answers from a corpus — that's
          // `retrieval`, not the id "law". Any future retrieval-backed field
          // gets the coverage link with no change here.
          showCoverageLink={Boolean(pack.retrieval)}
        />
      ) : null}

      {/* ─── Result ─── */}
      {view === "result" ? (
        <div ref={resultRef} className="grid gap-4 scroll-mt-20">
          {phase === "running" ? (
            <RunningCard progress={progress} stageLabel={stageLabel} elapsedMs={elapsedMs} />
          ) : null}

          {phase === "error" && error ? (
            <Card variant="outlined" className="border-error p-5">
              <div className="flex items-start gap-3">
                <Icon name="error" size={24} className="shrink-0 text-error" filled />
                <div className="min-w-0 flex-1">
                  <h3 className="md-title-medium text-on-surface">
                    {error.code === "quota_exhausted" ? "خلصت حصتك" : "ما زبطت"}
                  </h3>
                  <p className="md-body-medium mt-1 text-on-surface-variant">{error.message}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {error.code === "quota_exhausted" ? (
                      <Button icon="login" onClick={() => openAuth()}>
                        سجّل دخول وكمّل
                      </Button>
                    ) : null}
                    <Button variant="outlined" icon="edit" onClick={() => setView("form")}>
                      رجوع للاستمارة
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ) : null}

          {phase === "done" && result ? (
            <>
              <ResultView pack={pack} result={result} />
              <RerunBar
                onEdit={() => {
                  setView("form");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                onNew={() => {
                  reset();
                  clearPersisted(fieldId);
                  setPrefill(undefined);
                  setView("form");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                result={result}
                signedIn={Boolean(user)}
                navigate={navigate}
              />
            </>
          ) : null}
        </div>
      ) : null}

      {/* ─── Form (kept mounted so answers survive a trip to the result) ─── */}
      <div className={view === "result" ? "hidden" : ""}>
        <FormRenderer
          fieldId={fieldId}
          inputs={pack.inputs}
          initialValues={prefill}
          cost={pack.cost}
          remaining={remaining}
          quotaLimit={quota?.limit ?? null}
          detected={result?.detected}
          onDetect={onDetect}
          submitting={phase === "running"}
          onSubmit={(v) => void handleSubmit(v)}
        />
      </div>
    </div>
  );
}

// ─────────────────────────── pieces ───────────────────────────

function NoticeBanner({
  notice,
  navigate,
  showCoverageLink,
}: {
  notice: string;
  navigate: (r: Route) => void;
  showCoverageLink: boolean;
}) {
  // Packs write **bold** in their notice; render it rather than leaking asterisks.
  const parts = notice.split(/(\*\*[^*]+\*\*)/g);
  return (
    <Banner tone="primary" icon="balance" className="md-enter">
      <p>
        {parts.map((p, i) => {
          const m = /^\*\*([^*]+)\*\*$/.exec(p);
          return m ? <strong key={i}>{m[1]}</strong> : <span key={i}>{p}</span>;
        })}
      </p>
      {showCoverageLink ? (
        <Link
          to={{ name: "coverage" }}
          navigate={navigate}
          className="md-label-large mt-3 inline-flex items-center gap-1.5 rounded-full bg-on-primary-container/10 px-3 py-1.5 hover:bg-on-primary-container/20"
        >
          <Icon name="fact_check" size={16} />
          شوف شنو القوانين المتوفرة عدنا
        </Link>
      ) : null}
    </Banner>
  );
}

function RunningCard({
  progress,
  stageLabel,
  elapsedMs,
}: {
  progress: number;
  stageLabel: string;
  elapsedMs: number;
}) {
  return (
    <Card variant="filled" className="p-6">
      <div className="mb-4 flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary-container text-on-primary-container">
          <Icon name="autorenew" size={22} className="md-spin" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="md-title-medium text-on-surface">قاعدين نشتغل عليها…</p>
          <p className="md-body-small text-on-surface-variant" aria-live="polite">
            {stageLabel}
          </p>
        </div>
        <span className="md-body-small shrink-0 tabular-nums text-on-surface-variant">
          <bdi>{Math.floor(elapsedMs / 1000)}s</bdi>
        </span>
      </div>

      <LinearProgress value={progress} label="جاري التشغيل" />

      <p className="md-body-small mt-4 text-on-surface-variant">
        عادةً تاخذ بين ٥ و٢٠ ثانية. لا تسكّر الصفحة — جوابك محفوظ.
      </p>

      <div className="mt-4 grid gap-2">
        <div className="h-3 w-full animate-pulse rounded-full bg-surface-highest" />
        <div className="h-3 w-4/5 animate-pulse rounded-full bg-surface-highest" />
        <div className="h-3 w-2/3 animate-pulse rounded-full bg-surface-highest" />
      </div>
    </Card>
  );
}

function RerunBar({
  onEdit,
  onNew,
  result,
  signedIn,
  navigate,
}: {
  onEdit: () => void;
  onNew: () => void;
  result: RunResult;
  signedIn: boolean;
  navigate: (r: Route) => void;
}) {
  return (
    <Card variant="filled" className="p-5">
      <div className="flex items-start gap-3">
        <Icon name="tune" size={22} className="mt-0.5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <h3 className="md-title-medium text-on-surface">مو مضبوطة؟ عدّل جواب واحد بس.</h3>
          <p className="md-body-small mt-1 text-on-surface-variant">
            هذا اللي تكدر تسويه بالاستمارة ومو بالمحادثة: بدّل نوع النص أو الجمهور وأعِد التشغيل
            — بدون ما تكتب كلشي من الأول.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button icon="edit" onClick={onEdit}>
              عدّل وأعِد التشغيل
            </Button>
            <Button variant="outlined" icon="add" onClick={onNew}>
              تشغيل جديد
            </Button>
            {signedIn ? (
              <Button
                variant="text"
                icon="history"
                onClick={() => navigate({ name: "history" })}
              >
                تاريخي
              </Button>
            ) : null}
          </div>
          {!signedIn && result.status === "ok" ? (
            <p className="md-body-small mt-3 text-on-surface-variant">
              <Icon name="info" size={14} className="me-1 align-middle" />
              سجّل دخول حتى ينحفظ هذا التشغيل بتاريخك.
            </p>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
